'use client';

/**
 * DataGrid toolbar — Phase A1.
 *
 * Top strip of the grid:
 *   [Title • count]      [search box] [filter chips] [actions] [CSV]
 *
 * - Title + count anchor the surface so users always know what they're
 *   looking at + how many.
 * - Search is DEBOUNCED at 250ms. The grid hands the debounced value
 *   to the consumer's filter pipeline; we never mutate data ourselves.
 * - Filter chips render one per active filter. Each chip carries an
 *   X to clear that single dimension; a "Clear all" appears when any
 *   filter is active.
 * - The actions slot is a render-prop for the consumer (typically
 *   "+ New …" buttons, refresh, etc).
 * - CSV button surfaces only when `onExportCsv` is wired.
 *
 * All interactions are keyboard-accessible. Focus rings use the brand
 * `statura-green-accessible` token to match the rest of the UI.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ActiveFilters, FilterSpec } from './types';
import { labelForOption } from './filter-options';

export interface DataGridToolbarProps {
  title?: string;
  /** Total row count (server-reported total, not the page-visible count). */
  totalCount?: number;
  /** Initial value for the search box (controlled-by-default for tests). */
  searchValue?: string;
  /** Fired on debounced search changes (250 ms). Empty string = cleared. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Filter dimensions — drives chip rendering. */
  filters?: ReadonlyArray<FilterSpec>;
  /** Active values keyed by `FilterSpec.id`. */
  activeFilters?: ActiveFilters;
  /** Consumer wires this to mutate / reset filter values. */
  onFilterClear?: (filterId: string) => void;
  /** Optional "Clear all" handler. If omitted, button is hidden. */
  onClearAllFilters?: () => void;
  /** Render-prop slot for action buttons (right side). */
  actions?: React.ReactNode;
  /** Show + wire the CSV export button. */
  onExportCsv?: () => void;
  /** Hide the search box (some grids are filter-only). */
  hideSearch?: boolean;
  /**
   * Phase F1 Sprint 1 — filter SETTER widgets rendered below the search
   * row when present. Distinct from the chips display row: chips show
   * what's ACTIVE; setters let the user CHANGE the values.
   */
  filterControlsSlot?: React.ReactNode;
  /**
   * Phase F1 Sprint 1 — column / density controls slot. Typically the
   * `<DataGridColumnControls />` widget rendered at the right side of
   * the actions row.
   */
  columnControlsSlot?: React.ReactNode;
}

export function DataGridToolbar({
  title,
  totalCount,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters,
  activeFilters,
  onFilterClear,
  onClearAllFilters,
  filterControlsSlot,
  columnControlsSlot,
  actions,
  onExportCsv,
  hideSearch,
}: DataGridToolbarProps) {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local input in sync if the parent resets `searchValue`
  // externally (e.g. a "Clear all filters" CTA).
  //
  // Codex Phase A pass 2 P2: ALSO cancel any pending debounce timer.
  // Without this, a user can type → parent resets searchValue → the
  // stale timeout fires 250ms later with the old typed value and
  // re-applies the search the parent just cleared.
  useEffect(() => {
    setLocalSearch(searchValue);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, [searchValue]);

  const handleSearchChange = useCallback(
    (raw: string) => {
      setLocalSearch(raw);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!onSearchChange) return;
      debounceRef.current = setTimeout(() => {
        onSearchChange(raw);
      }, 250);
    },
    [onSearchChange],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Compute the chips from filters × activeFilters. We render a chip
  // for any filter whose active value is "set" (non-empty / non-zero
  // length / non-null). Each filter type formats slightly differently;
  // see the helper below.
  const chips = (filters ?? [])
    .map((spec) => {
      const value = activeFilters?.[spec.id];
      const label = formatChipLabel(spec, value);
      return label ? { id: spec.id, label } : null;
    })
    .filter((x): x is { id: string; label: string } => x !== null);

  const hasActiveFilters = chips.length > 0 || (!!localSearch && localSearch.trim().length > 0);

  // Phase F4 S8 — suppress the toolbar shell entirely when nothing
  // would render in it. Otherwise FocusedList consumers that don't
  // opt into v0.2 controls see an empty bordered strip above the
  // table.
  // totalCount alone is NOT counted as content — it only renders inside
  // the title block, so without `title` there's nothing to show.
  const hasAnyToolbarContent =
    !!title ||
    !!actions ||
    !!onExportCsv ||
    !!filterControlsSlot ||
    !!columnControlsSlot ||
    (!hideSearch && !!onSearchChange) ||
    (filters && filters.length > 0) ||
    hasActiveFilters;
  if (!hasAnyToolbarContent) return null;

  return (
    <div
      className="experience-candidate-data-grid flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3"
      data-data-grid-toolbar
    >
      {/* Top row: title + count + actions */}
      <div className="flex flex-wrap items-center gap-3">
        {title && (
          <div className="flex min-w-0 basis-full items-baseline gap-2 sm:basis-auto">
            <h2 className="min-w-0 break-words font-heading text-base font-semibold leading-tight text-statura-ink [overflow-wrap:anywhere]">
              {title}
            </h2>
            {typeof totalCount === 'number' && (
              <span
                className="font-mono text-[11px] font-medium text-text-muted"
                aria-label={`${totalCount} total`}
              >
                {formatCount(totalCount)}
              </span>
            )}
          </div>
        )}

        {!hideSearch && onSearchChange && (
          <div className="relative order-3 flex w-full min-w-[12rem] items-center sm:order-none sm:ml-auto sm:max-w-sm sm:flex-1">
            <Search
              className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className={cn(
                'h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-2.5 text-xs text-statura-ink placeholder:text-text-muted',
                'focus:border-statura-green-accessible focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
              )}
            />
            {/* Result count announced politely as the worker types. Without it a
                screen-reader user filters a list and hears nothing — the row
                count changes silently. `totalCount` is the post-filter length,
                because DataGrid passes the already-filtered `data`. Added when
                /me/courses (which had its own announced search box) was retired
                into /me/training, so the capability moved rather than being lost. */}
            {typeof totalCount === 'number' && (
              <span aria-live="polite" aria-atomic="true" className="sr-only">
                {localSearch
                  ? `${totalCount} ${totalCount === 1 ? 'result' : 'results'} for ${localSearch}`
                  : `${totalCount} ${totalCount === 1 ? 'result' : 'results'}`}
              </span>
            )}
          </div>
        )}

        <div
          className={cn(
            'flex w-full min-w-0 flex-wrap items-center gap-1.5 sm:w-auto [&>*]:max-w-full [&>*]:min-w-0',
            !title && 'sm:ml-auto',
          )}
        >
          {actions}
          {columnControlsSlot}
          {onExportCsv && (
            <button
              type="button"
              onClick={onExportCsv}
              aria-label="Export visible rows as CSV"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-[11.5px] font-medium text-statura-ink',
                'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
              )}
            >
              <Download className="h-3 w-3" aria-hidden="true" />
              CSV
            </button>
          )}
        </div>
      </div>

      {/* Phase F1 Sprint 1 — filter setter widgets row. Rendered above
          chips so the user sets values here then sees the active chips
          summarising state below. */}
      {filterControlsSlot && (
        <div className="flex w-full flex-wrap items-center gap-1.5">{filterControlsSlot}</div>
      )}

      {/* Filter chips row — only when at least one active */}
      {hasActiveFilters && (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {chips.map((chip) =>
            // Codex Phase A pass 5 P3: render chips as non-interactive
            // labels when `onFilterClear` isn't wired. Avoids exposing
            // keyboard-focusable buttons with an X that do nothing.
            onFilterClear ? (
              <button
                key={chip.id}
                type="button"
                onClick={() => onFilterClear(chip.id)}
                aria-label={`Clear filter ${chip.label}`}
                className={cn(
                  'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-left text-[10.5px] font-medium text-statura-green-accessible whitespace-normal break-words [overflow-wrap:anywhere]',
                  'hover:bg-statura-green-accessible/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                )}
              >
                {chip.label}
                <X className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <span
                key={chip.id}
                className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-[10.5px] font-medium text-statura-green-accessible whitespace-normal break-words [overflow-wrap:anywhere]"
              >
                {chip.label}
              </span>
            ),
          )}
          {onClearAllFilters && (chips.length > 0 || !!localSearch.trim()) && (
            <button
              type="button"
              onClick={() => {
                // Codex Phase A pass 3 P3: Clear all must ALSO reset
                // local search state + cancel any pending debounce,
                // otherwise a typed-but-not-yet-debounced value
                // re-applies after the parent clears.
                setLocalSearch('');
                if (debounceRef.current) {
                  clearTimeout(debounceRef.current);
                  debounceRef.current = null;
                }
                // Also surface the empty search to the parent so its
                // controlled `searchValue` resets in sync with its
                // filters (one consistent reset).
                onSearchChange?.('');
                onClearAllFilters();
              }}
              className="ml-1 text-[10.5px] font-medium text-text-muted hover:text-statura-ink focus:outline-none focus-visible:text-statura-ink"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Format an active filter into a chip label. Returns null for
 * "no active value" states so the chip is hidden.
 */
function formatChipLabel(spec: FilterSpec, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  switch (spec.kind) {
    case 'text':
      return `${spec.label}: ${String(value)}`;
    case 'select':
      return `${spec.label}: ${labelForOption(spec, value as string)}`;
    case 'multi-select': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (arr.length === 0) return null;
      if (arr.length === 1) return `${spec.label}: ${labelForOption(spec, arr[0]!)}`;
      return `${spec.label}: ${arr.length} selected`;
    }
    case 'boolean':
      // Codex Phase A pass 2 P2: distinguish "filter is set to false"
      // (e.g. "Inactive only") from "filter is unset". Show a chip for
      // both true + false; only return null when value is undefined/
      // null/empty (handled by the early return above).
      return value === true ? spec.label : value === false ? `${spec.label}: No` : null;
    case 'date-range': {
      const v = value as { from?: string; to?: string } | null;
      if (!v) return null;
      if (v.from && v.to) return `${spec.label}: ${v.from} → ${v.to}`;
      if (v.from) return `${spec.label}: from ${v.from}`;
      if (v.to) return `${spec.label}: to ${v.to}`;
      return null;
    }
    default:
      return null;
  }
}

function formatCount(n: number): string {
  if (n < 10000) return n.toLocaleString('en-AU');
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 100000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
